import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Modal } from "./Modal";
import { Input } from "./Input";
import { Badge } from "./Badge";
import { Lock } from "lucide-react";
import {
  fetchEstadoSeguridad,
  fetchVerificarSegundoFactor,
} from "../../../peticiones_API/PeticionesSeguridad";
import { guardarDestinoTras2FA, rutaActual } from "../../../funciones/rutaRetorno";
import { guardarVentana, leerVentana, limpiarVentana, alCambiarVentana } from "../../../funciones/ventanaSegundoFactor";

/**
 * Fase A1 · LA PUERTA de los datos sensibles.
 *
 * El segundo factor no se exige por rol, sino POR LO QUE SE VA A ABRIR. Este componente es lo
 * ÚNICO común a las tres superficies que lo exigen —la pestaña Soporte, «mis contraseñas» y RRHH
 * cuando llegue—: pedir el código, abrir la ventana, enseñar el contador, e invitar a
 * configurarlo a quien no lo tenga.
 *
 * **Lo que hay detrás de la puerta NO vive aquí.** Cada pantalla enseña lo suyo: Soporte los
 * datos de otra persona, «mis contraseñas» los tuyos. Compartir más sería juntar cosas que sólo
 * se parecen.
 *
 * Es la única excepción autorizada a «cero componentes nuevos en ui-v2.5»: la alternativa era
 * escribirlo dos veces en MUI, una en el perfil y otra en la ficha, y dos implementaciones del
 * mismo bloque divergen.
 */

const SEGUNDOS_AVISO = 60;

/** mm:ss */
function reloj(segundos) {
  const s = Math.max(0, Math.floor(segundos));
  return `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;
}

/**
 * El estado de la ventana, para la pantalla que la usa.
 *
 * Devuelve el token mientras esté viva y `null` en cuanto caduca, así que una pantalla que
 * pregunte `if (!ventana)` se cierra sola sin tener que vigilar el reloj.
 */
export function useVentanaSegundoFactor() {
  // El estado ARRANCA de lo guardado, no de null. Es lo que hace que una ventana abierta en el
  // listado siga viva al entrar en una ficha: el componente es nuevo, la ventana no.
  const [dato, setDato] = useState(() => leerVentana());
  const [restante, setRestante] = useState(() => {
    const d = leerVentana();
    return d ? (d.expira - Date.now()) / 1000 : 0;
  });

  const abrir = useCallback((token, iso, minutos, extra) => {
    guardarVentana(token, iso, minutos, extra);
    setDato(leerVentana());
  }, []);

  const cerrar = useCallback(() => {
    limpiarVentana();
    setDato(null);
    setRestante(0);
  }, []);

  // Otra pantalla puede abrir o cerrar la ventana: el candado de la barra y el listado son
  // componentes distintos y tienen que ver lo mismo.
  useEffect(() => alCambiarVentana(() => setDato(leerVentana())), []);

  useEffect(() => {
    if (!dato) return undefined;
    const tic = () => {
      const quedan = (dato.expira - Date.now()) / 1000;
      setRestante(quedan);
      // Al agotarse se BORRA del almacén, no sólo se olvida: un token caducado ahí es basura
      // que confunde. `leerVentana` lo borra al leerlo, así que basta con releer.
      if (quedan <= 0) { limpiarVentana(); setDato(null); }
    };
    tic();
    const id = setInterval(tic, 1000);
    return () => clearInterval(id);
  }, [dato]);

  return {
    ventana: dato ? dato.token : null,
    minutos: dato ? dato.minutos : null,
    // Si el servidor deja alargarla y hasta cuándo: lo que pinta el «+N min» del candado.
    prorrogable: !!(dato && dato.prorrogable),
    tope: dato ? dato.tope : null,
    expira: dato ? dato.expira : null,
    restante,
    activa: !!dato,
    abrir,
    cerrar,
  };
}

/**
 * El contador. Visible mientras la ventana esté abierta, y cambia de tono en el último minuto
 * para que agotarse no sea una sorpresa.
 */
export function ContadorVentana({ restante, activa, minutos = null, className = "" }) {
  if (!activa) return null;
  const poco = restante <= SEGUNDOS_AVISO;
  return (
    <span className={`inline-flex items-center gap-2 ${className}`}>
      <Badge tone={poco ? "warning" : "info"}>
        Visible {reloj(restante)}
      </Badge>
      <span className="text-[12px] text-v25-ink-500">
        {poco
          ? "Al llegar a cero se ocultarán y habrá que verificar otra vez"
          : `Al agotarse se ocultarán solos${minutos ? ` · tu verificación dura ${minutos} min` : ""}`}
      </span>
    </span>
  );
}

/**
 * La puerta.
 *
 * `onAbierta(token, expiraEn)` se llama cuando la verificación sale bien. El componente NO
 * guarda el token: lo entrega y se cierra.
 */
export function PuertaSegundoFactor({
  open,
  onClose,
  onAbierta,
  titulo = "Verificación en dos pasos",
  queVasAVer = "estos datos",
}) {
  const [cargando, setCargando] = useState(true);
  const [configurado, setConfigurado] = useState(null);
  const [codigo, setCodigo] = useState("");
  const [error, setError] = useState(null);
  const [enviando, setEnviando] = useState(false);
  const inputRef = useRef(null);
  const navigate = useNavigate();

  /**
   * Llevar a configurarlo, y volver aquí al terminar.
   *
   * Antes esto era un enlace: decía dónde ir y no llevaba, que es media invitación. Y volver
   * importa — quien está en la ficha de una persona y se va a configurarse el segundo factor
   * no debería quedarse en el perfil teniendo que recordar qué iba a hacer.
   *
   * Se guarda con el mecanismo que el proyecto ya tiene para el retorno tras login
   * (`funciones/rutaRetorno`): misma puerta de validación contra open redirects, mismo consumo
   * de un solo uso y misma caducidad. Con cajón propio, porque compartirlo haría que un retorno
   * se comiera el otro.
   *
   * El destino se consume SOLO si la configuración termina. Abandonarla a medias y volver aquí
   * dejaría la invitación otra vez delante sin explicación.
   */
  const irAConfigurar = () => {
    guardarDestinoTras2FA(rutaActual());
    onClose?.();
    navigate("/perfil?tab=2");
  };

  // Al abrir se pregunta si lo tiene configurado, ANTES de enseñar el formulario: quien no lo
  // tenga debe ver la invitación, no un campo que nunca podrá rellenar.
  useEffect(() => {
    if (!open) return undefined;
    let vivo = true;
    setCargando(true); setError(null); setCodigo(""); setConfigurado(null);
    fetchEstadoSeguridad()
      .then((r) => { if (vivo) setConfigurado(!!r?.segundoFactorConfigurado); })
      .catch(() => { if (vivo) setConfigurado(null); })
      .finally(() => { if (vivo) setCargando(false); });
    return () => { vivo = false; };
  }, [open]);

  useEffect(() => {
    if (open && configurado) setTimeout(() => inputRef.current?.focus(), 50);
  }, [open, configurado]);

  const verificar = async (e) => {
    e?.preventDefault();
    if (enviando || codigo.trim().length < 6) return;
    setEnviando(true); setError(null);
    try {
      const r = await fetchVerificarSegundoFactor(codigo.trim());
      // ── LA PUERTA GUARDA LA VENTANA. Fase 12 · §6.7bis ────────────────────────────────────
      //
      // Antes devolvia el token y CONFIABA en que cada consumidor lo persistiera. Con tres
      // pantallas, dos lo hacian y una se olvido — y el olvido **no daba ningun error**: el
      // codigo se verificaba, la peticion devolvia 200, los datos llegaban, y el panel seguia
      // cerrado porque `activa` nunca pasaba a true.
      //
      // **Abrir la ventana es lo que hace la puerta.** Repartir esa responsabilidad es el mismo
      // patron que venimos corrigiendo: tres sitios que tienen que hacer lo mismo, y uno no.
      //
      // `guardarVentana` emite su propio evento, y TODOS los `useVentanaSegundoFactor` montados
      // lo escuchan — asi que no hay orden que respetar: el candado se entera igual que la
      // pantalla que abrio la puerta.
      // `prorrogable` y `tope` los decide el servidor (15/10): el candado sólo los enseña.
      guardarVentana(r.ventana, r.expiraEn, r.minutos, { prorrogable: r.prorrogable, tope: r.tope });
      // `onAbierta` se queda para lo que cada pantalla haga DESPUES —cargar sus datos, cerrar su
      // modal— pero ya no carga con la obligacion de persistir.
      onAbierta?.(r.ventana, r.expiraEn, r.minutos);
      onClose?.();
    } catch (err) {
      const codigoError = err?.response?.data?.error;
      // Quien no lo tenga configurado no recibe "código incorrecto": recibe la invitación. Es el
      // momento en que el requisito se entiende — acaba de pedir ver datos sensibles.
      if (codigoError === "SIN_SEGUNDO_FACTOR") setConfigurado(false);
      else setError(err?.response?.data?.message || "No se ha podido verificar el código");
      setCodigo("");
    } finally {
      setEnviando(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      size={configurado === false ? "md" : "sm"}
      title={configurado === false ? "Necesitas la verificación en dos pasos" : titulo}
      subtitle={
        configurado === false
          ? undefined
          : `Introduce el código de tu aplicación para ver ${queVasAVer}.`
      }
    >
      {cargando && <p className="text-[13px] text-v25-ink-500">Comprobando…</p>}

      {!cargando && configurado === false && (
        // El patron del bloque bloqueado del proyecto (mockup-ficha-persona-v6.html, .locked):
        // icono en circulo, titulo, explicacion centrada y atenuada, y la accion. Sin componentes
        // nuevos: es composicion con los tokens que ya existen.
        <div>
          <div className="px-2 pb-1 text-center">
            <div className="mx-auto mb-3 grid h-11 w-11 place-items-center rounded-full bg-v25-surface-raised">
              <Lock size={20} className="text-v25-ink-400" />
            </div>
            {/* Jerarquia: el PROBLEMA con peso, la SOLUCION atenuada debajo. Antes eran dos
                parrafos del mismo peso y se leian como uno solo. */}
            <h4 className="mb-1.5 text-[15px] font-semibold text-v25-ink-900">
              Te falta la verificación en dos pasos
            </h4>
            <p className="mx-auto max-w-[440px] text-[13px] text-v25-ink-500">
              Para ver {queVasAVer} hace falta tenerla configurada, y aún no la tienes activada.
              Se configura una sola vez con la aplicación de autenticación de tu móvil, y a partir
              de ahí te bastará un código de seis dígitos.
            </p>
          </div>

          <div className="mt-4 flex items-center justify-end gap-2 border-t border-v25-line pt-3">
            <span className="mr-auto text-[12px] text-v25-ink-500">Al terminar volverás aquí.</span>
            <button type="button" onClick={onClose}
              className="whitespace-nowrap rounded-v25-md border border-v25-line px-3 py-2 text-[13px] hover:bg-v25-surface-sunken">
              Ahora no
            </button>
            <button
              type="button"
              onClick={irAConfigurar}
              className="inline-flex items-center whitespace-nowrap rounded-v25-md bg-v25-brand-600 px-4 py-2 text-[13px] font-medium text-white shadow-v25-btn hover:bg-v25-brand-800"
            >
              Configurarla ahora
            </button>
          </div>
        </div>
      )}

      {/* SIN <form>, a proposito. El Modal ya usa portal, asi que hoy no habria anidamiento —
          pero un <form> dentro de otro es HTML invalido con portal o sin el, y el arreglo de esta
          puerta no debe depender de una decision de otro componente: si alguien quita el portal
          manana, el fallo volveria. El Enter se maneja en el campo. */}
      {!cargando && configurado === true && (
        <div className="space-y-3">
          <Input
            ref={inputRef}
            label="Código de verificación"
            hint="Seis dígitos de tu aplicación de autenticación, o uno de tus códigos de respaldo"
            value={codigo}
            onChange={(e) => setCodigo(e.target.value)}
            error={error}
            mono
            autoComplete="one-time-code"
            inputMode="text"
            placeholder="000000"
            onKeyDown={(e) => { if (e.key === 'Enter') verificar(e); }}
          />
          <div className="flex justify-end gap-2 pt-1">
            <button type="button" onClick={onClose}
              className="rounded-lg border border-v25-line px-3 py-2 text-[13px]">
              Cancelar
            </button>
            <button type="button" onClick={verificar} disabled={enviando || codigo.trim().length < 6}
              className="rounded-lg bg-v25-brand-600 px-3 py-2 text-[13px] font-medium text-white disabled:opacity-50">
              {enviando ? "Verificando…" : "Verificar"}
            </button>
          </div>
        </div>
      )}

      {!cargando && configurado === null && (
        <p className="text-[13px] text-v25-expired-fg">
          No se ha podido comprobar tu verificación en dos pasos. Inténtalo de nuevo.
        </p>
      )}
    </Modal>
  );
}
